export type CinematicActId = "beginning" | "middle" | "end";

export interface CinematicScene {
  id: string;
  position: number;
  start_ms?: number;
  director?: { master_story_position?: number | null } | null;
}

export interface CinematicAct {
  id: CinematicActId;
  title: string;
  sceneIds: string[];
  /** The source story's own words for where this act begins; never generated here. */
  logline?: string;
}

const ACTS = [
  { id: "beginning", title: "Beginning" },
  { id: "middle", title: "Middle" },
  { id: "end", title: "End" },
] as const satisfies readonly { id: CinematicActId; title: string }[];

function sortValue(value: number | undefined): number {
  return value !== undefined && Number.isFinite(value)
    ? value
    : Number.POSITIVE_INFINITY;
}

/** Group source scenes into editorial acts without assigning new story content. */
export function buildCinematicActs(
  scenes: readonly CinematicScene[],
): CinematicAct[] {
  const ordered = scenes
    .map((scene, index) => ({ scene, index }))
    .sort(
      (a, b) =>
        sortValue(a.scene.position) - sortValue(b.scene.position) ||
        sortValue(a.scene.start_ms) - sortValue(b.scene.start_ms) ||
        a.index - b.index,
    );

  if (ordered.length === 0) return [];

  const availableActs =
    ordered.length === 1
      ? [ACTS[0]]
      : ordered.length === 2
        ? [ACTS[0], ACTS[2]]
        : ACTS;
  const acts = availableActs.map(({ id, title }) => ({
    id,
    title,
    sceneIds: [] as string[],
  }));

  if (ordered.length < 3) {
    ordered.forEach(({ scene }, index) => acts[index]!.sceneIds.push(scene.id));
    return acts;
  }

  const masterPositions = ordered.map(
    ({ scene }) => scene.director?.master_story_position,
  );
  const validMasterChapters = masterPositions.every(
    (position, index) =>
      typeof position === "number" &&
      Number.isInteger(position) &&
      position >= 0 &&
      (index === 0 || position >= (masterPositions[index - 1] as number)),
  );
  const chapters = validMasterChapters
    ? [...new Set(masterPositions as number[])]
    : [];

  if (chapters.length >= 3) {
    const chapterActs = new Map(
      chapters.map((position, index) => [
        position,
        Math.floor((index * 3) / chapters.length),
      ]),
    );
    ordered.forEach(({ scene }, index) => {
      const chapter = masterPositions[index] as number;
      acts[chapterActs.get(chapter)!]!.sceneIds.push(scene.id);
    });
  } else {
    ordered.forEach(({ scene }, index) => {
      acts[Math.floor((index * 3) / ordered.length)]!.sceneIds.push(scene.id);
    });
  }

  return acts;
}

export interface MasterStoryBeat {
  position?: number;
  narrative_beat?: string | null;
}

function firstSentence(text: string, limit = 170): string {
  const clean = text.replace(/\s+/g, " ").trim();
  const sentence = clean.match(/^.+?[.!?](?=\s|$)/)?.[0] ?? clean;
  return sentence.length > limit
    ? `${sentence.slice(0, limit - 1).replace(/\s+\S*$/, "")}…`
    : sentence;
}

/** Quote the approved master story where each act begins, so chapter copy is the film's own. */
export function withActLoglines(
  acts: CinematicAct[],
  scenes: readonly CinematicScene[],
  masterStory: readonly MasterStoryBeat[] | null | undefined,
): CinematicAct[] {
  if (!masterStory?.length) return acts;
  const beats = new Map(
    masterStory
      .filter((beat) => typeof beat.position === "number" && beat.narrative_beat?.trim())
      .map((beat) => [beat.position!, beat.narrative_beat!]),
  );
  const sceneById = new Map(scenes.map((scene) => [scene.id, scene]));
  return acts.map((act) => {
    const opening = sceneById.get(act.sceneIds[0] ?? "");
    const beat = beats.get(opening?.director?.master_story_position ?? -1);
    return beat ? { ...act, logline: firstSentence(beat) } : act;
  });
}
